/**
 * "Used in Courses" slide-in panel (NA-284) — shown when an author clicks the
 * Lessons list's "Used In" cell. Mirrors QuestionUsagePanel (the Question
 * Bank's lessons-usage panel): header with the lesson title as context, a
 * list of courses (title, "C{n}" id, lifecycle badge), and a footer noting
 * removal isn't done from here plus the total count.
 */
import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { adminButton, adminSidePanel, adminStatusStyle, adminTokens } from '../admin-ui';
import { WorkflowStatusBadge, deriveBadgeState } from './WorkflowStatusBadge';
import { useModalFocusTrap } from './useModalFocusTrap';
import type { LessonCourseUsage } from '../api/courseRecordApi';
import { formatCourseHumanId } from '../utils/humanIds';

export interface LessonUsagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shown as context under the panel title — the lesson this usage belongs to. */
  lessonTitle: string;
  usage: LessonCourseUsage;
  /** Usage request in flight — show a loading body instead of "0 courses". */
  isLoading?: boolean;
  /** Usage request failed — show an error body with a retry action. */
  isError?: boolean;
  onRetry?: () => void;
}

export function LessonUsagePanel({
  isOpen,
  onClose,
  lessonTitle,
  usage,
  isLoading = false,
  isError = false,
  onRetry,
}: LessonUsagePanelProps): React.ReactElement {
  const { modalRef, handleKeyDown } = useModalFocusTrap({ isOpen, onClose });

  const styles = useMemo((): Record<string, CSSProperties> => ({
    ...adminSidePanel,
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 1200,
      display: 'flex',
      justifyContent: 'flex-end',
      pointerEvents: isOpen ? 'auto' : 'none',
    },
    scrim: {
      ...adminSidePanel.scrim,
      opacity: isOpen ? 1 : 0,
      transition: 'opacity 0.2s ease',
    },
    panel: {
      ...adminSidePanel.panel,
      position: 'relative',
      width: '420px',
      maxWidth: '100%',
      height: '100%',
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.2s ease',
    },
    errorText: {
      margin: `${adminTokens.spacing.lg} ${adminTokens.spacing.xl} 0`,
    },
    retryButton: {
      ...adminButton.secondary,
      alignSelf: 'flex-start',
      margin: `${adminTokens.spacing.md} ${adminTokens.spacing.xl}`,
    },
  }), [isOpen]);

  // While closed, `inert` drops the off-screen panel's links and buttons out
  // of the tab order and accessibility tree (aria-hidden alone leaves them
  // tabbable); the component stays mounted for the slide transition and so
  // the focus trap can restore focus on close.
  return (
    <div style={styles.overlay} aria-hidden={!isOpen} inert={!isOpen} onKeyDown={handleKeyDown}>
      <div style={styles.scrim} onClick={onClose} role="presentation" />
      <div
        ref={modalRef}
        tabIndex={-1}
        style={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-usage-panel-title"
        aria-describedby="lesson-usage-panel-subtitle"
      >
        <div style={styles.header}>
          <div style={styles.headerText}>
            <h2 id="lesson-usage-panel-title" style={styles.title}>Used in Courses</h2>
            <p id="lesson-usage-panel-subtitle" style={styles.subtitle}>{lessonTitle}</p>
          </div>
          <button type="button" style={styles.closeButton} onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>

        <div style={styles.body}>
          {isLoading ? (
            <p role="status" style={styles.stateText}>Loading courses…</p>
          ) : isError ? (
            <>
              <p role="alert" style={{ ...adminStatusStyle('error'), ...styles.errorText }}>Couldn't load the courses using this lesson.</p>
              <button type="button" style={styles.retryButton} onClick={onRetry}>
                Retry
              </button>
            </>
          ) : usage.courses.length === 0 ? (
            <p style={styles.stateText}>Not used in any courses.</p>
          ) : (
            usage.courses.map((course) => (
              <div key={course.courseId} style={styles.row}>
                <div style={styles.rowText}>
                  <Link to={`/courses/${course.courseId}`} style={styles.rowLink}>
                    {course.title || 'Untitled Course'}
                  </Link>
                  <p style={styles.rowMeta}>{formatCourseHumanId(course.courseNumericId, course.courseId)}</p>
                </div>
                <WorkflowStatusBadge state={deriveBadgeState(course.workflowState, course.publishLifecycle)} />
              </div>
            ))
          )}
        </div>

        <div style={styles.footer}>
          <p style={styles.footerNote}>To remove this lesson from a course, edit the course directly.</p>
          {!isLoading && !isError && (
            <span style={styles.footerCount}>{usage.count} {usage.count === 1 ? 'course' : 'courses'}</span>
          )}
        </div>
      </div>
    </div>
  );
}

LessonUsagePanel.displayName = 'LessonUsagePanel';
